import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Flex, Button, Text, IconButton,
  Popover, PopoverTrigger, PopoverContent, PopoverBody,
  InputGroup, InputLeftElement, Input, useOutsideClick
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { LuCheck, LuX } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { DateRangeSelector } from './DateRangeSelector';
import FilterMenu, { renderFilterPanel, getFilterIcon } from './FilterMenu';
import AdvancedDatePicker from './AdvancedDatePicker';
import { formatChipValues, getFilterDisplayName, getOperatorLabel } from './filterDisplayHelpers';
import { isCheckboxFilterType, getAvailableOperators, FILTER_OPERATORS } from '../../../utils/filterConstants';
import DisplayCurrencySelector from '../DisplayCurrencySelector';

// Linear's filter chip: 22px segments inside a hairline (24px in all),
// rounded 6px, a hairline between the segments too. At night the stronger
// hairline, or it vanishes.
const CHIP_LINE = { borderColor: 'redesign.border', _dark: { borderColor: 'redesign.border.strong' } };
const CHIP = {
  display: 'flex',
  alignItems: 'center',
  h: '24px',
  borderRadius: '6px',
  overflow: 'hidden',
  border: '1px solid',
  bg: 'redesign.bg.raised',
  fontSize: '12px',
  flexShrink: 0,
  ...CHIP_LINE,
};
// Keyboard focus is a 1px blue hairline inside the segment, not the browser's
// thick ring, which would overflow a 22px chip.
const FOCUS_RING = { outline: 'none', boxShadow: 'inset 0 0 0 1px var(--chakra-colors-redesign-accent-blue)' };
const SEGMENT = { display: 'flex', alignItems: 'center', h: '100%', px: '6px', whiteSpace: 'nowrap', _focusVisible: FOCUS_RING };
const DIVIDED = { borderLeft: '1px solid', ...CHIP_LINE };
const SEGMENT_HOVER = { bg: 'redesign.bg.hover' };
// Linear's filled pill (and/or, + Filter, the advanced chip): no hairline, a
// fill one step off the ground. At night the fill is the strong hairline's
// colour, and the label is the body ink so it still reads at 4.5:1.
const PILL_FILL = {
  bg: 'redesign.bg.card',
  _dark: { bg: 'redesign.border.strong', color: 'redesign.text' },
};

/** The chip's last segment: Linear's thin × in a 22px slot. */
const ChipRemove = ({ onClick, label, divided = true }) => (
  <Box
    as="button"
    type="button"
    {...SEGMENT}
    {...(divided && DIVIDED)}
    w="22px"
    px={0}
    justifyContent="center"
    color="redesign.text.muted"
    _hover={{ ...SEGMENT_HOVER, color: 'redesign.heading' }}
    onClick={onClick}
    aria-label={label}
  >
    <Box as={LuX} boxSize="12px" />
  </Box>
);

/** "and" / "or" between conditions: a pill that flips the logic when clicked. */
const ConjPill = ({ value, onClick }) => {
  const { t } = useTranslation('filters');
  return (
    <Box
      as="button"
      type="button"
      h="24px"
      px="8px"
      borderRadius="6px"
      fontSize="12px"
      color="redesign.text.muted"
      {...PILL_FILL}
      flexShrink={0}
      _hover={{ bg: 'redesign.bg.hover', color: 'redesign.heading' }}
      _focusVisible={FOCUS_RING}
      onClick={onClick}
    >
      {t(`chip.logical.${value || 'and'}`)}
    </Box>
  );
};

/**
 * A condition in the advanced panel: the same segmented chip as the bar's,
 * read-only but for its ×.
 */
const ConditionChip = ({ condition, filterOptions, onRemove }) => {
  const { t } = useTranslation('filters');
  const filterDef = filterOptions?.find(
    fo => fo.entity === condition.entity && fo.column === condition.column
  );
  const FieldIcon = getFilterIcon(condition);
  return (
    <Box {...CHIP}>
      <Box {...SEGMENT} pl="7px" gap="5px" color="redesign.text">
        <Box as={FieldIcon} boxSize="12px" color="redesign.text.muted" flexShrink={0} />
        {getFilterDisplayName(condition, filterOptions)}
      </Box>
      <Box {...SEGMENT} {...DIVIDED} color="redesign.text.muted">{getOperatorLabel(condition, filterDef)}</Box>
      <Box {...SEGMENT} {...DIVIDED} color="redesign.heading">{formatChipValues(condition, filterOptions)}</Box>
      <ChipRemove onClick={onRemove} label={t('chip.removeAria')} />
    </Box>
  );
};

/**
 * FilterBar - Combined filter UI component
 *
 * Layout matches Linear: toolbar in bordered box, filter chips in a separate clean row below.
 * Checkbox-type filters render as segmented chips: [Name] [operator ▾] [Values] [×]
 * Multiple filters of the same type are allowed (e.g. "Category is X" and "Category is not Y").
 */
export function FilterBar({
  dateRange,
  onNavigatePeriod,
  onModeChange,
  onCustomRange,
  onToday,
  filterOptions,
  filterOptionsLoading,
  selectedFilters,
  onAddFilter,
  onAppendFilter,
  onRemoveFilter,
  onUpdateFilter,
  onClearFilters,
  timePresets,
  showDateNav = true,
  // MI-298: the ≈ display-currency selector. On by default; Movements opts out
  // for now while its converted total is still being reworked.
  showDisplayCurrency = true,
  // Advanced mode props
  isAdvancedMode = false,
  filterGroups = [],
  groupsOperator = 'and',
  onEnableAdvancedMode,
  onDisableAdvancedMode,
  onAddFilterGroup,
  onRemoveFilterGroup,
  onToggleGroupOperator,
  onToggleGroupsOperator,
  onAddGroupCondition,
  onRemoveGroupCondition,
  searchText,
  onSearchChange,
  onSearchSubmit,
  // Where the filter control sits in the toolbar row: 'start' (before the
  // page's own controls) or 'end'.
  filterPlacement = 'end',
  // Value-first menu capabilities. Optional: without them the menu still
  // matches values locally, just without counts or the AI row.
  onApplySuggestion,
  onRemoveLastFilter,
  fetchSuggestions,
  onAiFilter,
  onParsePeriod,
  children
}) {
  const { t } = useTranslation('filters');
  const hasActiveFilters = selectedFilters.length > 0 || isAdvancedMode;

  // Unified popover state — only one popover can be open at a time
  // null | { type: 'chip', index } | { type: 'chipOperator', index } | { type: 'advanced' }
  const [activePopover, setActivePopover] = useState(null);
  const [advancedPicker, setAdvancedPicker] = useState(null);

  const closePopover = useCallback(() => setActivePopover(null), []);

  // Linear closes the advanced panel on any click outside it. Blur alone
  // misses some: removing a group takes its focused × with it, and focus then
  // leaves the panel without a blur, so the panel listens for the click itself.
  const advancedPanelRef = useRef(null);
  const advancedChipRef = useRef(null);
  useOutsideClick({
    ref: advancedPanelRef,
    enabled: activePopover?.type === 'advanced',
    handler: (event) => {
      if (advancedChipRef.current?.contains(event.target)) return;
      closePopover();
    },
  });

  // Count only group conditions (root filters show as individual chips)
  const groupConditionCount = filterGroups.reduce((sum, g) => sum + g.conditions.length, 0);
  // With one condition the bar's chip reads it ("Account is Nequi"), as Linear's does.
  const onlyCondition = groupConditionCount === 1
    ? filterGroups.find(g => g.conditions.length)?.conditions[0]
    : null;
  const onlyConditionDef = onlyCondition && filterOptions?.find(
    fo => fo.entity === onlyCondition.entity && fo.column === onlyCondition.column
  );

  // Auto-create first empty group when entering advanced mode with none
  useEffect(() => {
    if (isAdvancedMode && filterGroups.length === 0 && activePopover?.type === 'advanced') {
      onAddFilterGroup();
    }
  }, [isAdvancedMode, filterGroups.length, activePopover, onAddFilterGroup]);

  // Handle chip filter update (index-based, for editing existing chips)
  const handleChipFilterUpdate = useCallback((index, filter) => {
    if (onUpdateFilter) {
      const updates = { values: filter.values, operator: filter.operator };
      if (filter.presetLabel !== undefined) updates.presetLabel = filter.presetLabel;
      onUpdateFilter(index, updates);
    } else {
      onAddFilter(filter);
    }
  }, [onUpdateFilter, onAddFilter]);

  // Handle operator change from the segmented chip dropdown
  const handleOperatorChange = useCallback((index, newOperator) => {
    if (onUpdateFilter) {
      const wasEmpty = selectedFilters[index]?.operator === FILTER_OPERATORS.IS_NULL;
      if (newOperator === FILTER_OPERATORS.IS_NULL) {
        onUpdateFilter(index, { operator: newOperator, values: [true] });
      } else if (wasEmpty) {
        onUpdateFilter(index, { operator: newOperator, values: [] });
      } else {
        onUpdateFilter(index, { operator: newOperator });
      }
    }
    closePopover();
  }, [onUpdateFilter, closePopover, selectedFilters]);

  // Movements puts the filter first, the dashboard puts it last, so the page
  // decides where in the row it lands.
  // Both entry points — the toolbar icon and the + in the chip row — must carry
  // the same wiring, or one of them silently falls back to replacing the chip
  // that is already on that field.
  const renderFilterMenu = (variant, overrides = {}) => (
    <FilterMenu
      variant={variant}
      filterOptions={filterOptions}
      filterOptionsLoading={filterOptionsLoading}
      selectedFilters={selectedFilters}
      onAddFilter={onAddFilter}
      onAppendFilter={onAppendFilter}
      onUpdateFilter={onUpdateFilter}
      onRemoveFilter={onRemoveFilter}
      onApplySuggestion={onApplySuggestion}
      onRemoveLastFilter={onRemoveLastFilter}
      onMenuOpen={closePopover}
      timePresets={timePresets}
      isAdvancedMode={isAdvancedMode}
      onEnableAdvancedMode={onEnableAdvancedMode}
      onOpenAdvancedPicker={setAdvancedPicker}
      fetchSuggestions={fetchSuggestions}
      onAiFilter={onAiFilter}
      {...overrides}
    />
  );

  const filterMenu = renderFilterMenu('search-icon', {
    openOnFind: true,
    onEnableAdvancedMode: () => { onEnableAdvancedMode(); setActivePopover({ type: 'advanced' }); },
  });

  return (
    <Box>
      {/* ── Toolbar ── */}
      <Box>
        <Flex
          justify="space-between"
          align="center"
          gap={{ base: 1, md: 3 }}
          py={2}
        >
          {/* Left: Date range selector */}
          {showDateNav && (
            <DateRangeSelector
              dateRange={dateRange}
              onNavigate={onNavigatePeriod}
              onModeChange={onModeChange}
              onCustomRange={onCustomRange}
              onToday={onToday}
              onParsePeriod={onParsePeriod}
            />
          )}

          {/* Right: Display currency (MI-298) + Search + Filter + action button */}
          <Flex gap={2} align="center" ml="auto">
            {showDisplayCurrency && <DisplayCurrencySelector />}
            {onSearchChange && (
              <InputGroup size="sm" flex={1} minW="60px" maxW="200px">
                <InputLeftElement pointerEvents="none" h="28px">
                  <SearchIcon color="redesign.text.muted" boxSize={3} />
                </InputLeftElement>
                <Input
                  placeholder={t('search.placeholder')}
                  value={searchText || ''}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && onSearchSubmit && e.target.value.trim()) {
                      onSearchSubmit(e.target.value.trim());
                    }
                  }}
                  borderRadius="md"
                  fontSize={{ base: "xs", md: "13px" }}
                  h="28px"
                />
              </InputGroup>
            )}
            {filterPlacement === 'start' && filterMenu}
            {children}
            {filterPlacement === 'end' && filterMenu}
          </Flex>
        </Flex>
      </Box>

      {/* ── Filter row: Linear's band under the toolbar, chips then + and Clear ── */}
      {hasActiveFilters && (
        <Flex
          gap="6px"
          mt={1}
          mb={2}
          px="10px"
          py="10px"
          bg="redesign.bg.card"
          borderRadius="8px"
          flexWrap="wrap"
          align="center"
        >
          {/* Regular filter chips */}
          {selectedFilters.map((filter, index) => {
            const filterDef = filterOptions?.find(
              fo => fo.entity === filter.entity && fo.column === filter.column
            );
            const isCheckbox = isCheckboxFilterType(filterDef);
            const operators = getAvailableOperators(filterDef);
            const isChipOpen = activePopover?.type === 'chip' && activePopover.index === index;
            const isOperatorOpen = activePopover?.type === 'chipOperator' && activePopover.index === index;
            const remove = () => { onRemoveFilter(index); closePopover(); };

            if (operators.length > 0) {
              // ── Segmented chip: [Icon Name] [operator] [Values] [×] ──
              const currentOp = operators.find(o => o.value === filter.operator) || operators[0];
              const FilterIcon = getFilterIcon(filter);
              const panelProps = {
                selectedFilters,
                onAddFilter: (f) => handleChipFilterUpdate(index, f),
                timePresets,
                onClose: closePopover,
                initialOperator: filter.operator,
                initialValue: filter.values?.[0],
                onOpenAdvancedPicker: setAdvancedPicker,
                // Checkbox-specific: scope values to this chip
                ...(isCheckbox && {
                  existingValues: filter.values || []
                })
              };

              return (
                <Box key={`${filter.entity}-${filter.column}-${filter.operator}-${index}`} {...CHIP}>
                  {/* Name segment — static label */}
                  <Box {...SEGMENT} pl="7px" gap="5px" color="redesign.text">
                    <Box as={FilterIcon} boxSize="12px" color="redesign.text.muted" flexShrink={0} />
                    {getFilterDisplayName(filter, filterOptions)}
                  </Box>

                  {/* Operator segment — opens the operator list. Linear draws
                      it as the bare word, with no caret. */}
                  <Popover
                    isOpen={isOperatorOpen}
                    onClose={closePopover}
                    placement="bottom-start"
                    isLazy
                  >
                    <PopoverTrigger>
                      <Box
                        as="button"
                        type="button"
                        {...SEGMENT}
                        {...DIVIDED}
                        // 5.5px a side: "is" measures 21px between dividers in Linear.
                        px="5.5px"
                        color="redesign.text.muted"
                        _hover={{ ...SEGMENT_HOVER, color: 'redesign.heading' }}
                        onClick={() => setActivePopover(isOperatorOpen ? null : { type: 'chipOperator', index })}
                      >
                        {currentOp?.label || filter.operator}
                      </Box>
                    </PopoverTrigger>
                    <PopoverContent w="auto" minW="150px" maxW="220px" borderRadius="10px">
                      <PopoverBody p="6px">
                        {operators.map(op => (
                          <Flex
                            key={op.value}
                            align="center"
                            gap="10px"
                            h="28px"
                            px="8px"
                            cursor="pointer"
                            borderRadius="6px"
                            _hover={{ bg: "redesign.bg.hover" }}
                            onClick={() => handleOperatorChange(index, op.value)}
                          >
                            <Text fontSize="13px" color="redesign.text" flex={1}>{op.label}</Text>
                            {op.value === filter.operator && (
                              <Box as={LuCheck} boxSize="14px" color="redesign.text.muted" flexShrink={0} />
                            )}
                          </Flex>
                        ))}
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>

                  {/* Values segment — opens values panel. "Is empty" has none. */}
                  {filter.operator !== FILTER_OPERATORS.IS_NULL && (
                  <Popover
                    isOpen={isChipOpen}
                    onClose={closePopover}
                    placement="bottom-start"
                    isLazy
                  >
                    <PopoverTrigger>
                      <Box
                        as="button"
                        type="button"
                        {...SEGMENT}
                        {...DIVIDED}
                        color="redesign.heading"
                        _hover={SEGMENT_HOVER}
                        onClick={() => setActivePopover(isChipOpen ? null : { type: 'chip', index })}
                      >
                        {formatChipValues(filter, filterOptions)}
                      </Box>
                    </PopoverTrigger>
                    {filterDef && (
                      <>
                        {isChipOpen && (
                          <Box position="fixed" top={0} left={0} right={0} bottom={0} zIndex={9} onClick={closePopover} />
                        )}
                        <PopoverContent w="240px" zIndex={10} borderRadius="10px">
                          <PopoverBody p={0}>
                            {renderFilterPanel(filterDef, panelProps)}
                          </PopoverBody>
                        </PopoverContent>
                      </>
                    )}
                  </Popover>
                  )}

                  <ChipRemove onClick={remove} label={t('chip.removeAria')} />
                </Box>
              );
            }

            // ── Fallback: one segment for filters with no operator options ──
            return (
              <Popover
                key={`${filter.entity}-${filter.column}-${filter.operator}-${index}`}
                placement="bottom-start"
                isOpen={isChipOpen}
                onClose={closePopover}
                isLazy
              >
                <Box {...CHIP}>
                  <PopoverTrigger>
                    <Box
                      as="button"
                      type="button"
                      {...SEGMENT}
                      pl="7px"
                      gap="4px"
                      color="redesign.text"
                      _hover={SEGMENT_HOVER}
                      onClick={() => setActivePopover(isChipOpen ? null : { type: 'chip', index })}
                    >
                      {getFilterDisplayName(filter, filterOptions)}
                      <Box as="span" color="redesign.text.muted">{getOperatorLabel(filter, filterDef)}</Box>
                      <Box as="span" color="redesign.heading">{formatChipValues(filter, filterOptions)}</Box>
                    </Box>
                  </PopoverTrigger>
                  <ChipRemove onClick={remove} label={t('chip.removeAria')} />
                </Box>
                {filterDef && (
                  <PopoverContent w="240px" borderRadius="10px">
                    <PopoverBody p={0}>
                      {renderFilterPanel(filterDef, {
                        selectedFilters,
                        onAddFilter: (f) => handleChipFilterUpdate(index, f),
                        timePresets,
                        onClose: closePopover,
                        initialOperator: filter.operator,
                        initialValue: filter.values?.[0],
                        onOpenAdvancedPicker: setAdvancedPicker
                      })}
                    </PopoverBody>
                  </PopoverContent>
                )}
              </Popover>
            );
          })}

          {/* Advanced filter: one chip in the bar that reads the condition when
              there is one, and opens Linear's panel of conditions. */}
          {isAdvancedMode && (
            <Popover
              placement="bottom-start"
              isOpen={activePopover?.type === 'advanced'}
              onClose={closePopover}
              isLazy
            >
              <Box ref={advancedChipRef} {...CHIP} border="none" bg="redesign.bg.raised">
                <PopoverTrigger>
                  <Box
                    as="button"
                    type="button"
                    {...SEGMENT}
                    pl="8px"
                    gap="4px"
                    color="redesign.text.muted"
                    _hover={{ ...SEGMENT_HOVER, color: 'redesign.heading' }}
                    onClick={() => setActivePopover(
                      activePopover?.type === 'advanced' ? null : { type: 'advanced' }
                    )}
                  >
                    {onlyCondition ? (
                      <>
                        <Box as="span" color="redesign.text">{getFilterDisplayName(onlyCondition, filterOptions)}</Box>
                        <Box as="span">{getOperatorLabel(onlyCondition, onlyConditionDef)}</Box>
                        <Box as="span" color="redesign.heading">{formatChipValues(onlyCondition, filterOptions)}</Box>
                      </>
                    ) : groupConditionCount > 0
                      ? t(groupConditionCount === 1 ? 'chip.conditionsOne' : 'chip.conditionsOther', { count: groupConditionCount })
                      : t('chip.advancedFilter')}
                  </Box>
                </PopoverTrigger>
                <ChipRemove
                  divided={false}
                  onClick={() => { closePopover(); onDisableAdvancedMode(); }}
                  label={t('chip.removeAria')}
                />
              </Box>
              <PopoverContent ref={advancedPanelRef} w="auto" minW="280px" maxW="520px" borderRadius="10px">
                <PopoverBody p="8px">
                  {filterGroups.map((group, groupIndex) => {
                    // One group reads as Linear's flat list; several become
                    // boxed groups joined by their own and/or.
                    const boxed = filterGroups.length > 1;
                    return (
                      <React.Fragment key={group.id}>
                        {groupIndex > 0 && (
                          <Box my="6px">
                            <ConjPill value={groupsOperator} onClick={onToggleGroupsOperator} />
                          </Box>
                        )}
                        <Box
                          position="relative"
                          {...(boxed && {
                            border: '1px solid',
                            borderColor: 'redesign.border',
                            borderRadius: '8px',
                            p: '6px',
                            pr: '32px',
                          })}
                        >
                          <Flex direction="column" align="flex-start" gap="6px">
                            {group.conditions.map((condition, idx) => (
                              <Flex key={`g-${group.id}-${idx}`} align="center" gap="6px">
                                {idx > 0 && (
                                  <ConjPill value={group.operator} onClick={() => onToggleGroupOperator(group.id)} />
                                )}
                                <ConditionChip
                                  condition={condition}
                                  filterOptions={filterOptions}
                                  onRemove={() => onRemoveGroupCondition(group.id, idx)}
                                />
                              </Flex>
                            ))}
                            <Flex align="center" gap="6px">
                              {group.conditions.length > 0 && (
                                <ConjPill value={group.operator} onClick={() => onToggleGroupOperator(group.id)} />
                              )}
                              <FilterMenu
                                variant="text"
                                filterOptions={filterOptions}
                                filterOptionsLoading={filterOptionsLoading}
                                selectedFilters={group.conditions}
                                onAddFilter={(c) => onAddGroupCondition(group.id, c)}
                                onAddFilterGroup={onAddFilterGroup}
                                timePresets={timePresets}
                              />
                            </Flex>
                          </Flex>
                          {boxed && (
                            <IconButton
                              icon={<LuX size={12} />}
                              size="xs"
                              variant="ghost"
                              position="absolute"
                              top="6px"
                              right="6px"
                              w="22px"
                              minW="22px"
                              h="22px"
                              borderRadius="6px"
                              color="redesign.text.muted"
                              onClick={() => onRemoveFilterGroup(group.id)}
                              aria-label={t('chip.removeGroup')}
                            />
                          )}
                        </Box>
                      </React.Fragment>
                    );
                  })}
                </PopoverBody>
              </PopoverContent>
            </Popover>
          )}

          {/* + button (always adds regular root filters) */}
          {renderFilterMenu('icon')}

          {/* Clear */}
          <Button
            size="xs"
            variant="ghost"
            h="24px"
            px="8px"
            borderRadius="6px"
            fontSize="12px"
            fontWeight="500"
            color="redesign.text"
            ml="auto"
            onClick={onClearFilters}
            _hover={{ bg: "redesign.bg.hover", color: "redesign.heading" }}
          >
            {t('chip.clearFilters')}
          </Button>
        </Flex>
      )}

      {advancedPicker && (
        <AdvancedDatePicker
          isOpen={true}
          onClose={() => setAdvancedPicker(null)}
          filter={advancedPicker.filter}
          initialOperator={advancedPicker.initialOperator}
          initialValue={advancedPicker.initialValue}
          onAddFilter={(data) => { onAddFilter(data); setAdvancedPicker(null); }}
        />
      )}
    </Box>
  );
}
